package io.jsonize.web.screens.editor

import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.files.File
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.math.min

data class EditorFileProgress(
    val fileName: String,
    val loadedBytes: Long,
    val totalBytes: Long,
    val done: Boolean
)

object EditorFileReader {

    // Existing simple full-read API (keep if already used elsewhere)
    suspend fun readFileText(
        file: File,
        onProgress: (EditorFileProgress?) -> Unit
    ): String = suspendCancellableCoroutine { cont ->
        val reader = org.w3c.files.FileReader()

        reader.onloadstart = {
            onProgress(
                EditorFileProgress(
                    fileName = file.name,
                    loadedBytes = 0,
                    totalBytes = file.size.toLong(),
                    done = false
                )
            )
        }

        reader.onprogress = { ev ->
            val dyn = ev.asDynamic()
            val loaded = (dyn.loaded as? Double)?.toLong() ?: 0L
            val total = (dyn.total as? Double)?.toLong() ?: file.size.toLong()
            onProgress(
                EditorFileProgress(
                    fileName = file.name,
                    loadedBytes = loaded,
                    totalBytes = total,
                    done = false
                )
            )
        }

        reader.onload = {
            val txt = reader.result as? String ?: ""
            onProgress(
                EditorFileProgress(
                    fileName = file.name,
                    loadedBytes = file.size.toLong(),
                    totalBytes = file.size.toLong(),
                    done = true
                )
            )
            cont.resume(txt)
        }

        reader.onerror = {
            onProgress(null)
            cont.resumeWithException(Exception("Failed to read file"))
        }

        cont.invokeOnCancellation {
            try { reader.abort() } catch (_: Throwable) {}
        }

        reader.readAsText(file)
    }

    // ✅ NEW: progressive reader – reads & emits chunks as they are decoded
    suspend fun readFileProgressive(
        file: File,
        chunkSize: Int = 64 * 1024,
        onChunk: (chunk: String, progress: EditorFileProgress) -> Unit
    ) {
        val total = file.size.toLong()
        var loaded = 0L
        var offset = 0L

        while (offset < total) {
            val end = min(offset + chunkSize, total)
            val blob = file.slice(offset.toInt(), end.toInt())

            val chunkText = readBlobText(blob)
            loaded = end

            val prog = EditorFileProgress(
                fileName = file.name,
                loadedBytes = loaded,
                totalBytes = total,
                done = loaded == total
            )

            onChunk(chunkText, prog)
            offset = end
        }
    }

    // Bridge JS Blob.text() Promise → suspend
    private suspend fun readBlobText(blob: dynamic): String =
        suspendCancellableCoroutine { cont ->
            val reader = org.w3c.files.FileReader()

            reader.onload = {
                val txt = reader.result as? String ?: ""
                cont.resume(txt)
            }

            reader.onerror = {
                cont.resumeWithException(Exception("Failed to read blob text"))
            }

            cont.invokeOnCancellation {
                try { reader.abort() } catch (_: Throwable) {}
            }

            // Works for File, Blob & sliced Blobs
            reader.readAsText(blob)
        }
}
